// AnlageDiagramme.tsx — Diagramme zu den Beziehungskarten der Kontaktanlage
// (User 26.09.2026):
//  - Beziehungsdiagramm mit Umschalter „Netzwerk" | „Familie"
//      Netzwerk: neuer Kontakt in der Mitte; durchgezogen = im Haushalt,
//      gestrichelt = weitere Beziehung, gepunktet = Verantwortung/GF bei
//      einem Unternehmen, Strich-Punkt = Firmenverbindung.
//      Familie: Stammbaum nach Generationen.
//  - Anlage-Organigramm: OrganigrammPopup mit einem Graphen aus den Einträgen,
//      dazu das gespeicherte Netzwerk bereits bestehender Firmen.
// Gezeichnet wird aus den noch nicht gespeicherten Einträgen (beziehungsAnzeige.ts).

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { REFINED } from "../risikoanalyseStep/tokens";
import { OrganigrammPopup } from "../redesign/OrganigrammPopup";
import "../redesign/contactRedesign.css";
import { fetchCompanyNetworkGraph, type CompanyNetworkGraph } from "../../services/companyNetworkService";
import {
  SELBST_ID,
  anlageNetz,
  anlageOrgGraph,
  alterAus,
  bestehendeFirmenIds,
  beziehungsLabel,
  geschlechtKurz,
  generationVon,
  graphenMischen,
  refName,
  type GeschlechtKurz,
  type NetzArt,
} from "./beziehungsAnzeige";
import type { ContactRef, FirmaConnItem, HouseholdItem, PersonFirmaItem } from "./types";
import type { VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

type MenschEintrag = { id: string; ref: ContactRef | null; verant: VerantwortlichkeitValue | null };

export type AnlageBeziehungen = {
  /**
   * Id des Kontakts, wenn es ihn schon gibt (Akte bearbeiten). Damit erkennt
   * das Organigramm ihn in den nachgeladenen Netzwerken wieder, statt ihn ein
   * zweites Mal zu zeichnen.
   */
  selbstId?: number;
  selbstName: string;
  selbstTyp: "Person" | "Firma";
  selbstAnrede?: string;
  selbstGeburtsdatum?: string;
  /** Der neue Kontakt selbst ist verstorben. */
  selbstVerstorben?: boolean;
  haushalt?: HouseholdItem[];
  weitere?: HouseholdItem[];
  personFirma?: PersonFirmaItem[];
  menschen?: MenschEintrag[];
  firmaConn?: FirmaConnItem[];
};

// ── Knopf oben rechts in der Karte ──────────────────────────────────────────

const OrgIcon = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="7.5" y="2.5" width="5" height="4" rx="1" />
    <rect x="2.5" y="13.5" width="5" height="4" rx="1" />
    <rect x="12.5" y="13.5" width="5" height="4" rx="1" />
    <path d="M10 6.5v3.5M5 13.5V10h10v3.5" />
  </svg>
);

/**
 * Kein <button>: Das Organigramm zeigt nur und muss sich auch in einer
 * gesperrten Akte (`<fieldset disabled>`) öffnen lassen — ein Knopf wäre dort
 * stumm (User-Finding 28.09.2026).
 */
export function DiagrammKnopf({ text, onClick }: { text: string; onClick: () => void }) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        border: `1.5px solid ${REFINED.aub}`,
        borderRadius: 9,
        background: REFINED.white,
        color: REFINED.aub,
        fontFamily: REFINED.fontUI,
        fontWeight: 600,
        fontSize: "0.82rem",
        padding: "5px 11px",
        cursor: "pointer",
      }}
    >
      {OrgIcon}
      {text}
    </span>
  );
}

// ── Rahmen ──────────────────────────────────────────────────────────────────

function Rahmen({
  titel,
  kopfRechts,
  children,
  onClose,
}: {
  titel: string;
  kopfRechts?: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    alleDokumente.addEventListener("keydown", onKey);
    return () => alleDokumente.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div
      role="presentation"
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100001,
        background: "rgba(20,8,20,0.34)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: REFINED.fontUI,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(980px, 100%)",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          background: REFINED.white,
          border: `1px solid ${REFINED.line}`,
          borderRadius: 14,
          boxShadow: REFINED.cardShadow,
          color: REFINED.ink,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "13px 18px",
            borderBottom: `2px solid ${REFINED.goldLine}`,
          }}
        >
          <h3 style={{ margin: 0, fontSize: REFINED.fs.lg, fontWeight: 800, flexGrow: 1 }}>{titel}</h3>
          {kopfRechts}
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            style={{ border: 0, background: "transparent", cursor: "pointer", color: REFINED.ink3, padding: 4 }}
          >
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </div>
        <div style={{ overflow: "auto", padding: 16 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// ── Netzwerk ────────────────────────────────────────────────────────────────

const LINIE: Record<NetzArt, { farbe: string; dash?: string; text: string }> = {
  haushalt: { farbe: "#7d2169", text: "im Haushalt" },
  weitere: { farbe: "#7d2169", dash: "7 5", text: "weitere Beziehung" },
  unternehmen: { farbe: "#b08a3e", dash: "2 5", text: "Verantwortung / GF im Unternehmen" },
  firma: { farbe: "#6e6270", dash: "9 4 2 4", text: "Firmenverbindung" },
};

const kurz = (t: string, n = 22) => (t.length > n ? t.slice(0, n - 1) + "…" : t);

/** Geschlecht als Symbol im Kreis — ohne Angabe bleibt der Kreis leer. */
const GESCHLECHT_SYMBOL: Record<Exclude<GeschlechtKurz, null>, { zeichen: string; text: string }> = {
  m: { zeichen: "\u2642", text: "männlich" },
  w: { zeichen: "\u2640", text: "weiblich" },
  d: { zeichen: "\u26A5", text: "divers" },
};

/** Kreuz für Verstorbene — steht VOR dem Geschlechts-Symbol (User 28.09.2026). */
export const KREUZ = "\u2020";

/**
 * Netzwerk wie beim Firmen-Organigramm: Knoten lassen sich frei ziehen, die
 * Linien folgen. Start ist ein Kreis um den neuen Kontakt.
 */
function NetzwerkAnsicht({ daten }: { daten: AnlageBeziehungen }) {
  const netz = React.useMemo(() => anlageNetz(daten), [daten]);
  const andere = netz.knoten.filter((k) => !k.selbst);
  const B = 900;
  const H = Math.max(500, 320 + andere.length * 14);

  const [pos, setPos] = React.useState<Map<string, { x: number; y: number }>>(() => {
    const cx = B / 2;
    const cy = H / 2;
    const rx = B / 2 - 130;
    const ry = H / 2 - 80;
    const m = new Map<string, { x: number; y: number }>([["self", { x: cx, y: cy }]]);
    andere.forEach((k, i) => {
      const w = -Math.PI / 2 + (2 * Math.PI * i) / Math.max(andere.length, 1);
      m.set(k.id, { x: cx + rx * Math.cos(w), y: cy + ry * Math.sin(w) });
    });
    return m;
  });
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const [ziehe, setZiehe] = React.useState<string | null>(null);

  const svgPunkt = (e: React.PointerEvent) => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
    return { x: Math.min(B - 30, Math.max(30, p.x)), y: Math.min(H - 50, Math.max(30, p.y)) };
  };

  if (!andere.length) {
    return <Leer text="Noch keine Beziehungen oder Verbindungen erfasst." />;
  }
  const arten = [...new Set(netz.kanten.map((k) => k.art))];
  return (
    <>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${B} ${H}`}
        style={{ width: "100%", height: "auto", display: "block", touchAction: "none", userSelect: "none" }}
        role="img"
        aria-label="Beziehungsnetzwerk"
        onPointerMove={(e) => {
          if (!ziehe) return;
          const p = svgPunkt(e);
          if (p) setPos((alt) => new Map(alt).set(ziehe, p));
        }}
        onPointerUp={() => setZiehe(null)}
        onPointerLeave={() => setZiehe(null)}
      >
        {netz.kanten.map((k, i) => {
          const a = pos.get(k.von)!;
          const b = pos.get(k.zu)!;
          const l = LINIE[k.art];
          return (
            <g key={i}>
              <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={l.farbe} strokeWidth={2} strokeDasharray={l.dash} strokeLinecap="round" />
              {k.label && (
                <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 5} textAnchor="middle" fontSize={12} fill={REFINED.ink2} stroke="#fff" strokeWidth={4} paintOrder="stroke" style={{ fontFamily: REFINED.fontUI }}>
                  {kurz(k.label, 34)}
                </text>
              )}
            </g>
          );
        })}
        {netz.knoten.map((k) => {
          const p = pos.get(k.id)!;
          const firma = k.typ === "Firma";
          const fuell = k.selbst ? "#7d2169" : firma ? "#a2586a" : "#6e6270";
          const r = k.selbst ? 28 : 21;
          const sym = k.geschlecht ? GESCHLECHT_SYMBOL[k.geschlecht] : null;
          const unten = firma ? 34 : r + 18;
          return (
            <g
              key={k.id}
              style={{ cursor: ziehe === k.id ? "grabbing" : "grab" }}
              onPointerDown={(e) => {
                (e.currentTarget.ownerSVGElement ?? e.currentTarget).setPointerCapture?.(e.pointerId);
                setZiehe(k.id);
              }}
            >
              {firma ? (
                <rect x={p.x - 24} y={p.y - 18} width={48} height={36} rx={8} fill={fuell} />
              ) : (
                <circle cx={p.x} cy={p.y} r={r} fill={fuell} />
              )}
              {/* Geschlecht nur, wenn bekannt — als kleines Symbol vor dem Namen. */}
              <text x={p.x} y={p.y + unten} textAnchor="middle" fontSize={13} fontWeight={k.selbst ? 800 : 600} fill={REFINED.ink} stroke="#fff" strokeWidth={4} paintOrder="stroke" style={{ fontFamily: REFINED.fontUI }}>
                {k.verstorben && (
                  <tspan fontSize={12} fontWeight={700} fill={REFINED.ink2}>
                    <title>verstorben</title>
                    {KREUZ}{" "}
                  </tspan>
                )}
                {sym && (
                  <tspan fontSize={11} fontWeight={600} fill={REFINED.ink3}>
                    <title>{sym.text}</title>
                    {sym.zeichen}{" "}
                  </tspan>
                )}
                {kurz(k.name)}
              </text>
              {k.alter != null && (
                <text x={p.x} y={p.y + unten + 15} textAnchor="middle" fontSize={12} fill={REFINED.ink3} stroke="#fff" strokeWidth={4} paintOrder="stroke" style={{ fontFamily: REFINED.fontUI }}>
                  {k.alter} Jahre
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Legende eintraege={arten.map((a) => ({ ...LINIE[a], art: a }))} />
      <div style={{ marginTop: 6, fontSize: "0.78rem", color: REFINED.ink3 }}>
        Knoten lassen sich mit der Maus verschieben.
      </div>
    </>
  );
}

function Legende({ eintraege }: { eintraege: Array<{ art: string; farbe: string; dash?: string; text: string }> }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 10, fontSize: "0.82rem", color: REFINED.ink2 }}>
      {eintraege.map((e) => (
        <span key={e.art} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <svg width="34" height="8" aria-hidden>
            <line x1="1" y1="4" x2="33" y2="4" stroke={e.farbe} strokeWidth={2} strokeDasharray={e.dash} strokeLinecap="round" />
          </svg>
          {e.text}
        </span>
      ))}
    </div>
  );
}

function Leer({ text }: { text: string }) {
  return <div style={{ padding: "40px 0", textAlign: "center", color: REFINED.ink3, fontStyle: "italic" }}>{text}</div>;
}

// ── Familie ─────────────────────────────────────────────────────────────────

const GENERATIONEN: Array<{ gen: number | null; titel: string }> = [
  { gen: 2, titel: "Großeltern" },
  { gen: 1, titel: "Eltern, Onkel & Tanten" },
  { gen: 0, titel: "Eigene Generation" },
  { gen: -1, titel: "Kinder, Neffen & Nichten" },
  { gen: -2, titel: "Enkel" },
  { gen: null, titel: "Weitere Beziehungen" },
];

function FamilienAnsicht({ daten }: { daten: AnlageBeziehungen }) {
  const personen = [
    ...(daten.haushalt ?? []).map((it) => ({ it, haushalt: true })),
    ...(daten.weitere ?? []).map((it) => ({ it, haushalt: false })),
  ].filter(({ it }) => refName(it.ref));

  if (!personen.length) return <Leer text="Noch keine Personen im oder außerhalb des Haushalts erfasst." />;

  const karte = (
    name: string,
    zusatz: string,
    haushalt: boolean | null,
    person: { geschlecht: GeschlechtKurz; alter: number | null; verstorben?: boolean },
    selbst = false,
  ) => (
    <div
      style={{
        minWidth: 150,
        maxWidth: 220,
        padding: "9px 12px",
        borderRadius: 10,
        border: selbst ? `2px solid ${REFINED.aub}` : `1.5px ${haushalt ? "solid" : "dashed"} ${REFINED.aub}`,
        background: selbst ? REFINED.aub : haushalt ? REFINED.aub50 : REFINED.white,
        color: selbst ? REFINED.white : REFINED.ink,
        textAlign: "center",
      }}
    >
      <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>
        {person.verstorben && (
          <span title="verstorben" aria-label="verstorben" style={{ marginRight: 4, fontSize: "0.8rem", fontWeight: 700 }}>
            {KREUZ}
          </span>
        )}
        {person.geschlecht && (
          <span title={GESCHLECHT_SYMBOL[person.geschlecht].text} style={{ marginRight: 4, fontSize: "0.72rem", fontWeight: 600, opacity: 0.7 }}>
            {GESCHLECHT_SYMBOL[person.geschlecht].zeichen}
          </span>
        )}
        {name}
      </div>
      <div style={{ fontSize: "0.78rem", opacity: 0.8 }}>
        {[zusatz, person.alter != null ? `${person.alter} Jahre` : ""].filter(Boolean).join(" · ")}
      </div>
    </div>
  );

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {GENERATIONEN.map(({ gen, titel }) => {
          const hier = personen.filter(({ it }) => generationVon(beziehungsLabel(it)) === gen);
          if (!hier.length && gen !== 0) return null;
          return (
            <div key={titel} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0", borderBottom: `1px solid ${REFINED.line}` }}>
              <div style={{ width: 150, flexShrink: 0, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: REFINED.ink3 }}>
                {titel}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", flexGrow: 1 }}>
                {gen === 0 &&
                  karte(
                    daten.selbstName || "Neuer Kontakt",
                    "dieser Kontakt",
                    null,
                    {
                      geschlecht: geschlechtKurz(null, daten.selbstAnrede),
                      alter: alterAus(daten.selbstGeburtsdatum),
                      verstorben: !!daten.selbstVerstorben,
                    },
                    true,
                  )}
                {hier.map(({ it, haushalt }) => (
                  <React.Fragment key={it.id}>
                    {karte(refName(it.ref), beziehungsLabel(it), haushalt, {
                      geschlecht: geschlechtKurz(it.ref?.gender, it.ref?.salutationLabel),
                      alter: alterAus(it.ref?.birthDate),
                      verstorben: !!it.ref?.deceased,
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <Legende
        eintraege={[
          { art: "haushalt", farbe: REFINED.aub, text: "im Haushalt (gefüllt, durchgezogen)" },
          { art: "weitere", farbe: REFINED.aub, dash: "7 5", text: "außerhalb des Haushalts (gestrichelt)" },
        ]}
      />
    </>
  );
}

// ── Dialoge ─────────────────────────────────────────────────────────────────

export function BeziehungsDiagramm({ daten, onClose }: { daten: AnlageBeziehungen; onClose: () => void }) {
  const [ansicht, setAnsicht] = React.useState<"netzwerk" | "familie">("netzwerk");
  const segment = (wert: "netzwerk" | "familie", text: string) => (
    <button
      type="button"
      aria-pressed={ansicht === wert}
      onClick={() => setAnsicht(wert)}
      style={{
        border: 0,
        borderRadius: 8,
        padding: "5px 12px",
        cursor: "pointer",
        fontFamily: REFINED.fontUI,
        fontWeight: 600,
        fontSize: "0.82rem",
        background: ansicht === wert ? REFINED.aub : "transparent",
        color: ansicht === wert ? REFINED.white : REFINED.aub,
      }}
    >
      {text}
    </button>
  );
  return (
    <Rahmen
      titel="Organigramm · Beziehungen"
      onClose={onClose}
      kopfRechts={
        <div role="group" aria-label="Ansicht" style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 10, background: REFINED.aub50 }}>
          {segment("netzwerk", "Netzwerk")}
          {segment("familie", "Familie")}
        </div>
      }
    >
      {ansicht === "netzwerk" ? <NetzwerkAnsicht daten={daten} /> : <FamilienAnsicht daten={daten} />}
    </Rahmen>
  );
}

/**
 * Organigramm der Anlage: die erfassten Verbindungen plus das gespeicherte
 * Netzwerk der bereits bestehenden Firmen. Lädt eine Firma nicht, bleibt ihr
 * Teil einfach weg — das Bild aus der Anlage steht trotzdem.
 */
export function AnlageOrganigramm({ daten, onClose }: { daten: AnlageBeziehungen; onClose: () => void }) {
  const basis = React.useMemo(() => anlageOrgGraph(daten), [daten]);
  const [graph, setGraph] = React.useState<CompanyNetworkGraph>(basis);
  const ids = React.useMemo(
    () =>
      bestehendeFirmenIds([
        ...(daten.personFirma ?? []).map((it) => it.ref),
        ...(daten.firmaConn ?? []).map((it) => it.ref),
        ...(daten.menschen ?? []).map((it) => it.ref),
      ]),
    [daten],
  );
  React.useEffect(() => {
    setGraph(basis);
    if (!ids.length) return;
    let aktiv = true;
    Promise.allSettled(ids.map((id) => fetchCompanyNetworkGraph(id))).then((ergebnisse) => {
      if (!aktiv) return;
      const geladen = ergebnisse.flatMap((e) => (e.status === "fulfilled" ? [e.value] : []));
      setGraph(graphenMischen(basis, geladen, daten.selbstId));
    });
    return () => {
      aktiv = false;
    };
  }, [basis, ids, daten.selbstId]);

  return (
    <OrganigrammPopup
      companyId={SELBST_ID}
      companyName={daten.selbstName || "Neuer Kontakt"}
      vorgabeGraph={graph}
      onClose={onClose}
    />
  );
}
